import { AreaChart, BarChart } from '@mantine/charts';
import {
  Anchor,
  Badge,
  Button,
  Divider,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatPenceToPounds, getBillingTypeLabel } from 'src/secret-menu/billing/billingUtils';
import { MetricHint, type MetricHintKey } from 'src/secret-menu/billing/MetricHint';
import type { BillingDetail } from 'src/secret-menu/billing/hooks/useBilling';
import { applyLens, engagementOf, indexSeries, trendOf, type Lens } from './lenses';
import {
  billingFor,
  formatPercent,
  percentOfStated,
  toArrPence,
  type RevenueModel,
} from './revenue';
import type { Firm, UsageModel } from './usageModel';

type Props = {
  model: UsageModel;
  firm: Firm;
  month: string;
  lens: Lens;
  today: Date;
  revenue: RevenueModel | null;
  onEditBilling: (detail: BillingDetail) => void;
  onBack: () => void;
};

const BREAKDOWN: { key: keyof Firm['totals']; label: string }[] = [
  { key: 'srs', label: 'SR creates' },
  { key: 'notes', label: 'Meeting notes' },
  { key: 'agendas', label: 'Agendas' },
  { key: 'recordings', label: 'Meetings recorded' },
  { key: 'files', label: 'Files uploaded' },
  { key: 'siesta', label: 'Siesta messages' },
];

function poundsOrDash(amount: number | null): string {
  return amount === null || amount === undefined ? '—' : `£${Number(amount).toFixed(2)}`;
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: MetricHintKey }) {
  return (
    <Stack gap={2}>
      <Group gap={4}>
        <Text size="textXs" c="dimmed">
          {label}
        </Text>
        {hint && <MetricHint hint={hint} />}
      </Group>
      <Text fw={FONT_WEIGHTS.semibold}>{value}</Text>
    </Stack>
  );
}

function RevenueCard({
  detail,
  onEdit,
}: {
  detail: BillingDetail | undefined;
  onEdit: (detail: BillingDetail) => void;
}) {
  if (!detail) {
    return (
      <Paper p="md" withBorder radius="md">
        <Text fw={FONT_WEIGHTS.semibold} mb={4}>
          Revenue
        </Text>
        <Text size="textXs" c="dimmed">
          This firm has no billing record, so it contributes nothing to MRR.
        </Text>
      </Paper>
    );
  }

  return (
    <Paper p="md" withBorder radius="md">
      <Group justify="space-between" mb="sm" wrap="wrap">
        <Text fw={FONT_WEIGHTS.semibold}>Revenue</Text>
        <Button size="xs" variant="light" onClick={() => onEdit(detail)}>
          Edit billing
        </Button>
      </Group>

      <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="md">
        <Stat label="Billing type" value={getBillingTypeLabel(detail.billingType)} />
        <Stat
          label="Billing amount"
          value={poundsOrDash(detail.billingAmount)}
          hint="billingAmount"
        />
        <Stat
          label="Minimum fee"
          value={poundsOrDash(detail.minimumBillingAmount)}
          hint="minimumFee"
        />
        <Stat
          label="Stated clients"
          hint="statedClients"
          value={
            detail.statedClientCount === null
              ? '—'
              : detail.statedClientCount.toLocaleString('en-GB')
          }
        />
        <Stat
          label="Active clients"
          value={detail.activeClientsCount.toLocaleString('en-GB')}
          hint="activeClients"
        />
        <Stat
          label="Billed units"
          hint="billedUnits"
          value={`${detail.activeBillableUnitCount.toLocaleString('en-GB')} billed of ${detail.billableUnitCount.toLocaleString('en-GB')}`}
        />
      </SimpleGrid>

      <Divider my="md" />

      <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="md">
        <Stat label="Monthly bill" value={formatPenceToPounds(detail.monthlyBillPence)} />
        <Stat label="ARR" value={formatPenceToPounds(toArrPence(detail.monthlyBillPence))} />
        <Stat
          label="Stated bill of all clients"
          value={
            detail.potentialMonthlyBillPence === null
              ? '—'
              : formatPenceToPounds(detail.potentialMonthlyBillPence)
          }
        />
        <Stat
          label="Stated ARR of all clients"
          value={
            detail.potentialMonthlyBillPence === null
              ? '—'
              : formatPenceToPounds(toArrPence(detail.potentialMonthlyBillPence))
          }
        />
        <Stat
          label="Clients loaded"
          value={formatPercent(percentOfStated(detail.billableUnitCount, detail.statedClientCount))}
        />
        <Stat
          label="Active of stated"
          value={formatPercent(
            percentOfStated(detail.activeBillableUnitCount, detail.statedClientCount),
          )}
        />
      </SimpleGrid>

      <Text size="textXs" c="dimmed" mt="sm">
        A unit is a family, or a client belonging to no family. Billed units are the ones holding an
        active client, which is what the bill is drawn from; all units are the whole book. Both are
        counted from live client data, so they cannot be edited here — only the billing amount and
        the minimum fee. Loaded percentages compare units against the clients the firm stated, so a
        firm whose clients are mostly in couples reads well below 100% while being fully loaded. The
        stated bill of all clients prices every stated client as its own unit, making it an upper
        bound rather than a forecast.
      </Text>
    </Paper>
  );
}

export default function FirmDetail({
  model,
  firm,
  month,
  lens,
  today,
  revenue,
  onEditBilling,
  onBack,
}: Props) {
  const chartLens: Lens = lens === 'share' ? 'raw' : lens;
  const months = model.months.filter(key => key >= firm.first);

  const rawSeries = months.map(key =>
    applyLens(firm.series.get(key)?.usage ?? 0, key, chartLens === 'indexed' ? 'raw' : chartLens, {
      model,
      today,
      firm,
    }),
  );
  const series = chartLens === 'indexed' ? indexSeries(rawSeries) : rawSeries;

  const usageData = months.map((key, index) => ({
    month: model.monthLabels.get(key) ?? key,
    usage: series[index],
  }));

  const counts = firm.series.get(month);
  const breakdownData = BREAKDOWN.map(item => ({
    metric: item.label,
    value: (counts?.[item.key] as number) ?? 0,
  }));

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Stack gap={2}>
          <Anchor size="textXs" onClick={onBack}>
            ← Back to all firms
          </Anchor>
          <Title order={3}>{firm.name}</Title>
          <Text size="textXs" c="dimmed">
            First active {model.monthLabels.get(firm.first)} · last active{' '}
            {model.monthLabels.get(firm.last)} · {firm.activeMonths} active months
          </Text>
        </Stack>
        <Group gap="xs">
          <Badge variant="light" color="gray">
            {engagementOf(counts?.usage ?? 0, counts?.newClients ?? 0)}
          </Badge>
          <Badge variant="light">{trendOf(firm, month, model)}</Badge>
        </Group>
      </Group>

      <RevenueCard detail={billingFor(revenue, firm.name)} onEdit={onEditBilling} />

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <Paper p="md" withBorder radius="md">
          <Text fw={FONT_WEIGHTS.semibold} mb="sm">
            Usage over time
          </Text>
          <AreaChart
            h={220}
            data={usageData}
            dataKey="month"
            series={[{ name: 'usage', label: 'Usage actions', color: 'brand.6' }]}
            curveType="linear"
            withDots={false}
            connectNulls={false}
          />
        </Paper>

        <Paper p="md" withBorder radius="md">
          <Text fw={FONT_WEIGHTS.semibold} mb="sm">
            What they use
          </Text>
          <Text size="textXs" c="dimmed" mb="sm">
            {model.monthLabels.get(month)}
          </Text>
          <BarChart
            h={200}
            data={breakdownData}
            dataKey="metric"
            orientation="vertical"
            series={[{ name: 'value', label: 'Actions', color: 'brand.6' }]}
          />
        </Paper>
      </SimpleGrid>

      <Paper p="md" withBorder radius="md">
        <Text fw={FONT_WEIGHTS.semibold} mb="sm">
          Month by month
        </Text>
        <Table.ScrollContainer minWidth={760}>
          <Table highlightOnHover verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Month</Table.Th>
                <Table.Th ta="right">Usage</Table.Th>
                <Table.Th ta="right">New clients</Table.Th>
                {BREAKDOWN.map(item => (
                  <Table.Th key={item.key} ta="right">
                    {item.label}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {[...months].reverse().map(key => {
                const monthCounts = firm.series.get(key);
                return (
                  <Table.Tr key={key}>
                    <Table.Td>{model.monthLabels.get(key)}</Table.Td>
                    <Table.Td ta="right" fw={FONT_WEIGHTS.semibold}>
                      {(monthCounts?.usage ?? 0).toLocaleString('en-GB')}
                    </Table.Td>
                    <Table.Td ta="right">
                      {(monthCounts?.newClients ?? 0).toLocaleString('en-GB')}
                    </Table.Td>
                    {BREAKDOWN.map(item => (
                      <Table.Td key={item.key} ta="right">
                        {((monthCounts?.[item.key] as number) ?? 0).toLocaleString('en-GB')}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>
    </Stack>
  );
}
